import { useSetAtom } from "jotai";
import { useRouter } from "next/router";
import { type FunctionComponent, useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { plusNewsAgendaAtom } from "scmp-app/components/plus/news-agenda/atoms";
import { useNewsAgendaContext } from "scmp-app/components/plus/news-agenda/contexts";
import { isUpdatedMoreThan15HoursAgo } from "scmp-app/components/plus/news-agenda/helpers";
import { useNewsAgenda } from "scmp-app/components/plus/news-agenda/hooks";
import { UpdatedLabel } from "scmp-app/components/plus/news-agenda/updated-label";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import { pathname as plusAgendaPathname } from "scmp-app/pages/plus/agenda";
import type { itemPlusNewsAgendaItemTopic$key } from "scmp-app/queries/__generated__/itemPlusNewsAgendaItemTopic.graphql";

import {
  Arrow,
  Container,
  Date,
  DateDetail,
  DateRange,
  Day,
  Detail,
  DetailRight,
  Month,
  More,
  Separator,
  Summary,
  Time,
  Title,
  UpdatedLabelContainer,
} from "./styles";

type Props = {
  className?: string;
  reference: itemPlusNewsAgendaItemTopic$key;
};

export const PlusNewsAgendaItem: FunctionComponent<Props> = ({
  className,
  reference: reference_,
}) => {
  const item = useFragment(
    graphql`
      fragment itemPlusNewsAgendaItemTopic on Topic {
        entityId
        name
        urlAlias
        updatedDate
        newsAgenda {
          secondaryDescription
          startDate
        }
        ...hooksNewsAgendaTopic
      }
    `,
    reference_,
  );

  const router = useRouter();
  const { currentAgendaId, setCurrentAgendaId } = useNewsAgendaContext();
  const { date, day, hasDetailsView, isKeyEvent, month, time } = useNewsAgenda(item);
  const isUpdated = isUpdatedMoreThan15HoursAgo(item.updatedDate);
  const setPlusNewsAgendaState = useSetAtom(plusNewsAgendaAtom);

  const isActiveItem = currentAgendaId === item?.entityId;

  const onClickHandler = useCallback(() => {
    if (!hasDetailsView) return;

    setCurrentAgendaId?.(item.entityId);

    setPlusNewsAgendaState(previousState => ({
      ...previousState,
      agendaEvent: item.name ?? "",
      agendaEventDate: date ?? "",
    }));

    sendGA4Tracking({
      action: "click",
      category: "menu",
      customized_parameters: {
        agenda_event: item.name,
        agenda_event_date: date ?? "",
        page_type: "agenda",
        topic_id: item.entityId,
      },
      subcategory: "agenda",
    });

    void router.push(
      {
        pathname: plusAgendaPathname,
        query: {
          entityId: item.entityId,
          startDate: item.newsAgenda?.startDate?.toFixed(0) ?? undefined,
        },
      },
      item.urlAlias,
      {
        scroll: false,
        shallow: true,
      },
    );
  }, [
    date,
    hasDetailsView,
    item.entityId,
    item.name,
    item.newsAgenda?.startDate,
    item.urlAlias,
    router,
    setCurrentAgendaId,
    setPlusNewsAgendaState,
  ]);

  if (!item) return null;

  return (
    <Container
      $isActive={isActiveItem}
      $withoutDetail={!hasDetailsView}
      className={className}
      key={item.entityId}
      onClick={onClickHandler}
    >
      <Date $isKeyEvent={isKeyEvent}>
        <Day>{day}</Day>
        <Month>{month}</Month>
      </Date>
      <Detail>
        <Title>{item.name}</Title>

        {isUpdated && (
          <UpdatedLabelContainer>
            <UpdatedLabel />
          </UpdatedLabelContainer>
        )}
        <DetailRight>
          <DateDetail>
            <DateRange>{date}</DateRange>
            {time && (
              <>
                <Separator>|</Separator>
                <Time>{time}</Time>
              </>
            )}
          </DateDetail>
          {item?.newsAgenda?.secondaryDescription && (
            <Summary>{item?.newsAgenda?.secondaryDescription}</Summary>
          )}
        </DetailRight>
      </Detail>
      {hasDetailsView && (
        <More>
          <Arrow />
        </More>
      )}
    </Container>
  );
};

PlusNewsAgendaItem.displayName = "PlusNewsAgendaItem";
